'use client';
import {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './ReadStory.module.scss';
import { useTranslation } from 'react-i18next';
import { Modal } from '../Modal/Modal';
import { Page } from '../../lib/pageData';
import classNames from 'classnames';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface ContinueReadingProps {
  maxHeight?: number;
  page: Page;
}

export const ReadStory = ({
  children,
  page,
}: PropsWithChildren<ContinueReadingProps>) => {
  const { t } = useTranslation();
  const divRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const MAX_HEIGHT = 320;
  const isMobile = useMediaQuery('screen and (max-width: 767px)');

  useEffect(() => {
    if (divRef.current && divRef.current.offsetHeight < MAX_HEIGHT) {
      setIsCollapsed(false);
    }
  }, [divRef.current?.offsetHeight]);

  type StoryModalProps = {
    children: ReactNode;
  };

  const StoryModal = ({ children }: StoryModalProps) => {
    return (
      <Modal
        withMaxWidth
        fullScreen={isMobile}
        onClose={() => setIsModalOpen(false)}
        open={isModalOpen}
      >
        <Modal.CloseButton fullScreen={true} />
        <Modal.Content>
          <Modal.Body>
            <h1 className="cp-heading-medium mt-0 mb-5">
              {page?.owner?.name
                ? t('readStory.modal.title', { name: page.owner?.name.split(' ')[0] })
                : t('story.title')}
            </h1>
            {children}
          </Modal.Body>
        </Modal.Content>
      </Modal>
    );
  };

  return (
    <>
      <div>
        <div
          ref={divRef}
          id="read-story"
          style={{ maxHeight: isCollapsed ? `${MAX_HEIGHT}px` : 'none' }}
          className={styles.content}
        >
          {children}
          {isCollapsed && <div className={styles.gradient}></div>}
        </div>

        {isCollapsed && (
          <button
            className={classNames('cp-text-link', styles.readStoryButton)}
            onClick={() => setIsModalOpen(true)}
          >
            {t('readStory.label')}
          </button>
        )}
      </div>
      {isModalOpen && <StoryModal>{children}</StoryModal>}
    </>
  );
};
